import React, { useContext } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AppLayout from "./components/AppLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import SessionExpiredModal from "./components/SessionExpiredModal";
import ToastContainer from "./components/common/ToastContainer";
import { ToastProvider } from "./context/ToastContext";
import { AuthContext } from "./context/AuthContext";
import { isAdminUser } from "./utils/roleUtils";
import AccessManagement from "./pages/AccessManagement";
import AdminHome from "./pages/AdminHome";
import EmployeeHome from "./pages/EmployeeHome";
import EmployeePurchasePrograms from "./pages/EmployeePurchasePrograms";
import KnowledgeBase from "./pages/KnowledgeBase";
import Login from "./pages/Login";
import NotFoundPage from "./pages/NotFoundPage";
import PasswordVault from "./pages/PasswordVault";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import DisplaySheetTool from "./tools/DisplaySheetTool";
import EmployeeReportTool from "./tools/EmployeeReportTool";
import EmployeeSpiffReportTool from "./tools/EmployeeSpiffReportTool";
import HRToolsHome from "./tools/HRToolsHome";
import MissingSkusTool from "./tools/MissingSkusTool";
import PurchaseOrderAllSheetsTool from "./tools/PurchaseOrderAllSheetsTool.jsx";
import PurchaseOrderTool from "./tools/PurchaseOrderTool";
import SalesFloorDisplayTool from "./tools/SalesFloorDisplayTool";
import SellThruReportTool from "./tools/SellThruReportTool";
import SkuBuilderAccessoriesTool from "./tools/SkuBuilderAccessoriesTool";
import SkuBuilderLacesTool from "./tools/SkuBuilderLacesTool";
import SkuBuilderTool from "./tools/SkuBuilderTool";
import SmartToolsHome from "./tools/SmartToolsHome";
import VariantReportTool from "./tools/VariantReportTool";
import OutletMoveTool from "./tools/OutletMoveTool";
import MapMasterSyncTool from "./tools/MapMasterSyncTool";
import MapUpdateHome from "./tools/MapUpdateHome";
import PriceUpdateTool from "./tools/PriceUpdateTool";
import TagUpdateTool from "./tools/TagUpdateTool";
import CustomersHome from "./tools/CustomersHome";
import CustomerLookupTool from "./tools/CustomerLookupTool";
import ReportsAnalyticsHome from "./tools/ReportsAnalyticsHome";
import LookbookAnalyticsTool from "./tools/LookbookAnalyticsTool";
import TagLookupTool from "./tools/TagLookupTool";
import MapStatusReconcileTool from "./tools/MapStatusReconcileTool";
import AmazonManagerHome from "./tools/AmazonManagerHome";
import AmazonListingsExportTool from "./tools/AmazonListingsExportTool";
import AmazonPricingBuyBoxTool from "./tools/AmazonPricingBuyBoxTool";
import AmazonMapMonitorTool from "./tools/AmazonMapMonitorTool";
import AmazonPriceUpdateTool from "./tools/AmazonPriceUpdateTool";
import AmazonInventoryUpdateTool from "./tools/AmazonInventoryUpdateTool";
import MapPolicyTool from "./tools/MapPolicyTool";
import FindProductsBySkuTool from "./tools/FindProductsBySkuTool";
import VendorMapReportTool from "./tools/VendorMapReportTool";
import MapReviewQueueTool from "./tools/MapReviewQueueTool";
import MapListTrackerTool from "./tools/MapListTrackerTool";
import FixVariantSkusTool from "./tools/FixVariantSkusTool";

function RootRedirect() {
  const { user, loading } = useContext(AuthContext);
  if (loading) return null;
  if (user) {
    return <Navigate to={isAdminUser(user) ? "/admin" : "/employee"} replace />;
  }
  return <Navigate to="/login" replace />;
}

function App() {
  const { forceLogout, logout } = useContext(AuthContext);

  return (
    <ToastProvider>
      <ToastContainer />
      <SessionExpiredModal show={forceLogout} onConfirm={logout} />

      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/employee" element={<EmployeeHome />} />

            <Route
              path="/vault"
              element={
                <ProtectedRoute itemId="password-vault">
                  <PasswordVault />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools"
              element={
                <ProtectedRoute itemId="smart-tools-home">
                  <SmartToolsHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/map-update"
              element={
                <ProtectedRoute itemId="map-update-home">
                  <MapUpdateHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/amazon-manager"
              element={
                <ProtectedRoute itemId="amazon-manager-home">
                  <AmazonManagerHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/amazon-listings-export"
              element={
                <ProtectedRoute itemId="amazon-listings-export">
                  <AmazonListingsExportTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/amazon-pricing-buybox"
              element={
                <ProtectedRoute itemId="amazon-pricing-buybox">
                  <AmazonPricingBuyBoxTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/amazon-map-monitor"
              element={
                <ProtectedRoute itemId="amazon-map-monitor">
                  <AmazonMapMonitorTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/amazon-price-update"
              element={
                <ProtectedRoute itemId="amazon-price-update">
                  <AmazonPriceUpdateTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/amazon-inventory-update"
              element={
                <ProtectedRoute itemId="amazon-inventory-update">
                  <AmazonInventoryUpdateTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/purchase-order"
              element={
                <ProtectedRoute itemId="purchase-order">
                  <PurchaseOrderTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/purchase-order-all-sheets"
              element={
                <ProtectedRoute itemId="purchase-order-all">
                  <PurchaseOrderAllSheetsTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/sku-builder"
              element={
                <ProtectedRoute itemId="sku-builder">
                  <SkuBuilderTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/sku-builder-accessories"
              element={
                <ProtectedRoute itemId="sku-builder-accessories">
                  <SkuBuilderAccessoriesTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/sku-builder-laces"
              element={
                <ProtectedRoute itemId="sku-builder-laces">
                  <SkuBuilderLacesTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/display-sheet"
              element={
                <ProtectedRoute itemId="display-sheet">
                  <DisplaySheetTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/missing-skus"
              element={
                <ProtectedRoute itemId="missing-skus">
                  <MissingSkusTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/variant-report"
              element={
                <ProtectedRoute itemId="variant-report">
                  <VariantReportTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/outlet-move"
              element={
                <ProtectedRoute itemId="outlet-move">
                  <OutletMoveTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/map-master-sync"
              element={
                <ProtectedRoute itemId="map-master-sync">
                  <MapMasterSyncTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/tag-update"
              element={
                <ProtectedRoute itemId="tag-update">
                  <TagUpdateTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/tag-lookup"
              element={
                <ProtectedRoute itemId="tag-lookup">
                  <TagLookupTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/reconcile-map-status"
              element={
                <ProtectedRoute itemId="reconcile-map-status">
                  <MapStatusReconcileTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/map-policy"
              element={
                <ProtectedRoute itemId="map-policy">
                  <MapPolicyTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/find-products-by-sku"
              element={
                <ProtectedRoute itemId="find-products-by-sku">
                  <FindProductsBySkuTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/vendor-map-report"
              element={
                <ProtectedRoute itemId="vendor-map-report">
                  <VendorMapReportTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/map-review-queue"
              element={
                <ProtectedRoute itemId="map-review-queue">
                  <MapReviewQueueTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/map-list-tracker"
              element={
                <ProtectedRoute itemId="map-list-tracker">
                  <MapListTrackerTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/price-update"
              element={
                <ProtectedRoute itemId="price-update">
                  <PriceUpdateTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/fix-variant-skus"
              element={
                <ProtectedRoute itemId="fix-variant-skus">
                  <FixVariantSkusTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/sales-floor-display"
              element={
                <ProtectedRoute itemId="sales-floor-display">
                  <SalesFloorDisplayTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/sell-thru-report"
              element={
                <ProtectedRoute itemId="sell-thru-report">
                  <SellThruReportTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase-programs"
              element={
                <ProtectedRoute itemId="purchase-programs">
                  <EmployeePurchasePrograms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kb"
              element={
                <ProtectedRoute itemId="knowledge-base">
                  <KnowledgeBase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute itemId="customers-home">
                  <CustomersHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/lookup"
              element={
                <ProtectedRoute itemId="customer-lookup">
                  <CustomerLookupTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute itemId="analytics-home">
                  <ReportsAnalyticsHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/lookbook"
              element={
                <ProtectedRoute itemId="lookbook-analytics">
                  <LookbookAnalyticsTool />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowedRoles={["Admin", "Super Admin"]} />}>
          <Route element={<AppLayout />}>
            <Route path="/admin" element={<AdminHome />} />
            <Route
              path="/hr-tools"
              element={
                <ProtectedRoute itemId="hr-tools-home">
                  <HRToolsHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/employee-report"
              element={
                <ProtectedRoute itemId="employee-report">
                  <EmployeeReportTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/employee-spiff-report"
              element={
                <ProtectedRoute itemId="employee-spiff-report">
                  <EmployeeSpiffReportTool />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roles"
              element={
                <ProtectedRoute itemId="access-management">
                  <AccessManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/access-management"
              element={
                <ProtectedRoute itemId="access-management">
                  <AccessManagement />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ToastProvider>
  );
}

export default App;
